/**
 * Operator domain per MIC — used only to fetch an exchange logo.
 *
 * Keyed to the OPERATOR, not the venue, because that is what a logo service
 * resolves: every Euronext book is euronext.com, every Nasdaq tier and the
 * Nordic exchanges are nasdaq.com, Toronto and TSX Venture are both tmx.com.
 * Two MICs sharing a domain is correct, not a duplicate.
 *
 * Not exhaustive by design, and deliberately not asserted complete: an
 * exchange with no recognisable operator site simply keeps the activity dot.
 * A missing logo is a smaller failure than a wrong one.
 */
export const EXCHANGE_DOMAINS: Readonly<Record<string, string>> = {
  // United States
  XNYS: 'nyse.com',
  NYSE: 'nyse.com',
  XASE: 'nyse.com',
  AMEX: 'nyse.com',
  ARCX: 'nyse.com',
  XNAS: 'nasdaq.com',
  XNGS: 'nasdaq.com',
  XNMS: 'nasdaq.com',
  XNCM: 'nasdaq.com',
  XPSX: 'nasdaq.com',
  OTCM: 'otcmarkets.com',
  XOTC: 'otcmarkets.com',
  PINX: 'otcmarkets.com',
  OOTC: 'otcmarkets.com',
  XCBO: 'cboe.com',
  BATS: 'cboe.com',
  IEXG: 'iex.io',
  // Canada
  XTSE: 'tmx.com',
  XTSX: 'tmx.com',
  // Latin America
  XMEX: 'bmv.com.mx',
  BVMF: 'b3.com.br',
  XSGO: 'bolsadesantiago.com',
  XBOG: 'bvc.com.co',
  XLIM: 'bvl.com.pe',
  // United Kingdom
  XLON: 'londonstockexchange.com',
  // Germany
  XETR: 'deutsche-boerse.com',
  XFRA: 'deutsche-boerse.com',
  XBER: 'boerse-berlin.de',
  // Euronext — one operator, one logo, seven books.
  XPAR: 'euronext.com',
  ALXP: 'euronext.com',
  XAMS: 'euronext.com',
  XBRU: 'euronext.com',
  XDUB: 'euronext.com',
  XLIS: 'euronext.com',
  XOSL: 'euronext.com',
  MERK: 'euronext.com',
  // Switzerland
  XSWX: 'six-group.com',
  XVTX: 'six-group.com',
  // Italy
  MTAA: 'borsaitaliana.it',
  XMIL: 'borsaitaliana.it',
  // Spain
  BMEX: 'bolsasymercados.es',
  XMAD: 'bolsasymercados.es',
  XMCE: 'bolsasymercados.es',
  // Nordics — Nasdaq operates Stockholm, Helsinki and Copenhagen.
  XSTO: 'nasdaq.com',
  SSME: 'nasdaq.com',
  XHEL: 'nasdaq.com',
  XCSE: 'nasdaq.com',
  // Austria
  XWBO: 'wienerborse.at',
  WBAH: 'wienerborse.at',
  // Central & Eastern Europe
  XATH: 'athexgroup.gr',
  XWAR: 'gpw.pl',
  XBUD: 'bse.hu',
  XPRA: 'pse.cz',
  // Turkey
  XIST: 'borsaistanbul.com',
  XEQY: 'borsaistanbul.com',
  // Russia
  MISX: 'moex.com',
  RTSX: 'moex.com',
  // Japan — JPX operates Tokyo, Osaka and the former JASDAQ.
  XTKS: 'jpx.co.jp',
  XOSE: 'jpx.co.jp',
  XJAS: 'jpx.co.jp',
  XNGO: 'nse.or.jp',
  XFKA: 'fse.or.jp',
  // Asia-Pacific
  XASX: 'asx.com.au',
  XHKG: 'hkex.com.hk',
  XSES: 'sgx.com',
  XNZE: 'nzx.com',
  // China
  XSSC: 'sse.com.cn',
  XSHG: 'sse.com.cn',
  XSHE: 'szse.cn',
  // Korea
  XKRX: 'krx.co.kr',
  XKOS: 'krx.co.kr',
  // Taiwan
  XTAI: 'twse.com.tw',
  ROCO: 'tpex.org.tw',
  // Southeast Asia
  XIDX: 'idx.co.id',
  XKLS: 'bursamalaysia.com',
  XBKK: 'set.or.th',
  XMAI: 'set.or.th',
  XPHS: 'pse.com.ph',
  // India
  XBOM: 'bseindia.com',
  XNSE: 'nseindia.com',
  // South & West Asia
  XKAR: 'psx.com.pk',
  XTAE: 'tase.co.il',
  // Africa
  XJSE: 'jse.co.za',
  XCAS: 'casablanca-bourse.com',
  XCAI: 'egx.com.eg',
  // Middle East
  DSMD: 'qe.com.qa',
  XKUW: 'boursakuwait.com.kw',
  XSAU: 'saudiexchange.sa',
  XADS: 'adx.ae',
  XDFM: 'dfm.ae',
  DIFX: 'nasdaqdubai.com',
};

export function exchangeDomain(mic: string): string | null {
  return EXCHANGE_DOMAINS[mic] ?? null;
}
